import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  AiRecommendationMessage,
  AgentSection,
  ConsultingSection,
  FooterCta,
  RequestAgentDialog,
} from "../../../components/ax-consulting";
import type { ConsultingOption, AgentCard } from "../../../components/ax-consulting";
import type { PublicAgentDetail, MainCategory } from "@/types/agent";
import type {
  ShortlistData,
  ShortlistReadiness,
  ShortlistItem,
} from "@/services/project-report-service";
import { getProjectShortlist } from "@/services/project-report-service";
import { SuggestCustomAgentDialog } from "./suggest-custom-agent-dialog";
import { requestCustomAgent } from "@/services/shortlist-service";
import { useToast } from "@/hooks/use-toast";

const toPublicAgent = (item: ShortlistItem): PublicAgentDetail => ({
  id: item.agent_id,
  name: item.metadata.name,
  provider: item.metadata.provider,
  short_description: item.metadata.short_description,
  solution: null,
  categories: item.metadata.categories,
  application_area: item.metadata.application_area,
  main_category: item.metadata.main_category,
  selected_count: 0,
  created_at: item.created_at,
  updated_at: null,
});

interface AxConsultingResultTabProps {
  shortlistData: ShortlistData | null;
  readiness: ShortlistReadiness | null;
  onRefresh?: () => void | Promise<void>;
}

export function AxConsultingResultTab({
  shortlistData,
  readiness,
  id,
  onRefresh,
}: AxConsultingResultTabProps & { id: string }) {
  const navigate = useNavigate();
  const [selectedConsulting, setSelectedConsulting] = useState<string | null>(null);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [isContactLoading, setIsContactLoading] = useState(false);
  const { toast } = useToast();

  const toggleAgent = (id: string) => {
    setSelectedAgents((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  const consultingOptions: ConsultingOption[] = useMemo(
    () =>
      (readiness?.low_score_sections ?? []).map((section, idx) => ({
        id: String(idx + 1),
        title: section.cta_name,
        badge: section.tag,
        badgeType: section.tag === "Best fit" ? "best-fit" : "high-rec",
        subtitle: section.corrective_actions,
        message_solution: section.message_solution,
        description: section.detailed_message,
      })),
    [readiness?.low_score_sections],
  );

  // Auto-select the first consulting option if options are available and none is selected
  useEffect(() => {
    if (consultingOptions.length > 0 && selectedConsulting === null) {
      setSelectedConsulting(consultingOptions[0].id);
    }
  }, [consultingOptions, selectedConsulting]);

  const aiRecommendationText = readiness ? `${readiness.interpretation}` : "";

  // Enabled if at least one agent is selected, and either there are no consulting options or one consulting option is selected
  const isContactEnabled =
    selectedAgents.length > 0 && (consultingOptions.length === 0 || selectedConsulting !== null);
  const hasConsulting = consultingOptions.length > 0;

  const handleContact = async () => {
    if (!isContactEnabled || isContactLoading) return;
    const selectedOption = consultingOptions.find((o) => o.id === selectedConsulting);
    setIsContactLoading(true);
    try {
      const res = await getProjectShortlist(id);
      const items = res.data.data.items;
      const selectedAgentIds = items
        .filter((item) => selectedAgents.includes(item.id))
        .map((item) => item.agent_id);
      const matched = items.filter((item) => selectedAgentIds.includes(item.agent_id));

      if (matched.length === 0) {
        const validSelectedIds = new Set(items.map((it) => it.id));
        setSelectedAgents((prev) => prev.filter((sid) => validSelectedIds.has(sid)));
        await onRefresh?.();
        toast({
          description: "에이전트가 삭제되었습니다",
          variant: "destructive",
        });
        return;
      }

      const agents = matched.map(toPublicAgent);
      navigate("/contact", {
        state: {
          agents,
          projectId: id,
          selectedConsulting: selectedOption?.title,
          projectName: shortlistData?.project_name,
        },
      });
    } catch (error) {
      console.error("Failed to fetch shortlist before contact:", error);
    } finally {
      setIsContactLoading(false);
    }
  };

  const handleSubmitCustomAgent = async (title: string, content: string) => {
    try {
      await requestCustomAgent({
        title,
        content,
        agent_ids: [],
        project_id: id,
      });

      toast({ description: "요청이 전송되었습니다." });
      setRequestDialogOpen(false);
    } catch (error) {
      toast({
        description: "오류가 발생했습니다. 다시 시도해 주세요.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-[1120px] min-[1120px]:px-0 mx-auto">
      <div className="flex flex-col gap-1">
        <h1 className="text-[30px] md:text-[32px] font-normal leading-[36px] md:leading-[40px] tracking-[-0.0213em] md:tracking-[-0.03em] text-[#202020]">
          당신의 비즈니스를 가속할 AX 추천
        </h1>
        <p className="text-sm leading-[20px] tracking-[-0.01em] text-[#000000]">
          요구사항 분석 결과를 토대로 PwC 컨설팅 서비스와 AI 에이전트를 함께 제안합니다.
        </p>
      </div>

      <div className="flex flex-col gap-12">
        {aiRecommendationText && (
          <AiRecommendationMessage title="AI Recommendation" description={aiRecommendationText} />
        )}

        {hasConsulting && (
          <ConsultingSection
            options={consultingOptions}
            selectedId={selectedConsulting}
            onSelect={setSelectedConsulting}
            hasMore={readiness?.has_more_low_sections || false} // 예시로 2개 초과 시 '더보기' 버튼 노출
          />
        )}

        <AgentSection
          shortlistData={shortlistData}
          selectedIds={selectedAgents}
          onToggle={toggleAgent}
          onRequestOtherAgent={() => setRequestDialogOpen(true)}
          projectId={id}
        />
      </div>

      <FooterCta enabled={isContactEnabled} loading={isContactLoading} onContact={handleContact} />

      {/* Dialog */}
      <SuggestCustomAgentDialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        onSubmit={handleSubmitCustomAgent}
        projectName={shortlistData?.project_name}
      />
    </div>
  );
}
